import { Head, Link, router } from '@inertiajs/react'
import { useState, useCallback, useEffect, type FormEvent } from 'react'
import { Users, Building2, Clock, MessageSquare, Activity, Mail, UserPlus, Search, X } from 'lucide-react'
import { FilterBuilder, FilterState, FilterableField } from '@/components/filters'
import NavigationRail from '@/components/NavigationRail'
import { formatRelativeTime, getInitials, getAvatarColor } from '@/lib/displayUtils'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import type { Agent } from '@/types/shared'

interface User {
  id: number
  name: string | null
  email: string | null
  companyId: number | null
  companyName: string | null
  locale: string
  lastSeenAt: string | null
  createdAt: string
  conversationsCount: number
  eventsCount: number
}

interface Props {
  users: User[]
  totalCount: number
  filteredCount: number
  leadsCount: number
  companiesCount: number
  filterableFields: FilterableField[]
  currentFilters: FilterState
  currentSearch: string | null
  currentAgent: Agent
  hasMore: boolean
  currentPage: number
}

export default function ContactsUsers({
  users: initialUsers,
  totalCount,
  filteredCount,
  leadsCount,
  companiesCount,
  filterableFields,
  currentFilters,
  currentSearch,
  currentAgent,
  hasMore: initialHasMore,
  currentPage: initialCurrentPage,
}: Props) {
  const [filters, setFilters] = useState<FilterState>(currentFilters);
  const [users, setUsers] = useState<User[]>(initialUsers);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState(currentSearch ?? '');
  const [isSearchOpen, setIsSearchOpen] = useState(!!currentSearch);

  // Reset state when filters change (server returns new data)
  useEffect(() => {
    setUsers(initialUsers);
    setHasMore(initialHasMore);
    setCurrentPage(initialCurrentPage);
  }, [initialUsers, initialHasMore, initialCurrentPage]);

  useEffect(() => {
    setSearchInput(currentSearch ?? '');
    setIsSearchOpen(!!currentSearch);
  }, [currentSearch]);

  const loadMoreUsers = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    const filtersJson = JSON.stringify(filters);
    const searchParam = currentSearch?.trim();

    try {
      const response = await fetch(
        `/contacts/users/more?page=${nextPage}&filters=${encodeURIComponent(filtersJson)}${
          searchParam ? `&search=${encodeURIComponent(searchParam)}` : ''
        }`
      );
      const data = await response.json();

      setUsers((prev) => [...prev, ...data.users]);
      setHasMore(data.hasMore);
      setCurrentPage(data.currentPage);
    } catch (error) {
      console.error('Error loading more users:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, currentPage, filters, currentSearch]);

  const { sentinelRef } = useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    onLoadMore: loadMoreUsers,
  });

  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);

    const filtersJson = JSON.stringify(newFilters);
    const searchParam = currentSearch?.trim();
    const url = searchParam
      ? `/contacts/users?filters=${encodeURIComponent(filtersJson)}&search=${encodeURIComponent(searchParam)}`
      : `/contacts/users?filters=${encodeURIComponent(filtersJson)}`;

    router.visit(url, {
      preserveState: true,
      preserveScroll: true,
    });
  }, [currentSearch]);

  const handleSearchSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = searchInput.trim();
      const filtersJson = JSON.stringify(filters);
      const url = trimmed
        ? `/contacts/users?filters=${encodeURIComponent(filtersJson)}&search=${encodeURIComponent(trimmed)}`
        : `/contacts/users?filters=${encodeURIComponent(filtersJson)}`;

      router.visit(url, {
        preserveState: true,
        preserveScroll: true,
      });
    },
    [filters, searchInput]
  );

  const handleSearchClear = useCallback(() => {
    setSearchInput('');
    const filtersJson = JSON.stringify(filters);
    router.visit(`/contacts/users?filters=${encodeURIComponent(filtersJson)}`, {
      preserveState: true,
      preserveScroll: true,
    });
  }, [filters]);

  const handleSearchToggle = useCallback(() => {
    if (currentSearch?.trim()) {
      setSearchInput('');
      setIsSearchOpen(false);
      const filtersJson = JSON.stringify(filters);
      router.visit(`/contacts/users?filters=${encodeURIComponent(filtersJson)}`, {
        preserveState: true,
        preserveScroll: true,
      });
      return;
    }

    setIsSearchOpen((prev) => !prev);
  }, [currentSearch, filters]);

  const isFiltered = !!currentSearch?.trim() || filters.filters.length > 0;

  return (
    <>
      <Head title="Usuários - Contatos" />

      <div className="h-screen flex bg-white">
        <NavigationRail currentAgent={currentAgent} activePage="contacts" />

        {/* Sidebar */}
        <div className="w-48 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-lg font-semibold text-gray-900">Contatos</h1>
          </div>

          <nav className="flex-1 p-2">
            <Link
              href="/contacts/users"
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg"
            >
              <Users className="w-5 h-5 shrink-0" />
              Usuários
              <span className="ml-auto text-xs text-blue-600">{totalCount}</span>
            </Link>
            <Link
              href="/contacts/leads"
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <UserPlus className="w-5 h-5 shrink-0" />
              Leads
              <span className="ml-auto text-xs text-gray-500">{leadsCount}</span>
            </Link>
            <Link
              href="/contacts/companies"
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <Building2 className="w-5 h-5 shrink-0" />
              Empresas
              <span className="ml-auto text-xs text-gray-500">{companiesCount}</span>
            </Link>
          </nav>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3 gap-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {isFiltered ? (
                  <>
                    {filteredCount.toLocaleString()} usuários correspondem de{' '}
                    {totalCount.toLocaleString()}
                  </>
                ) : (
                  <>
                    {totalCount.toLocaleString()} usuários
                  </>
                )}
              </h2>
              <div className="flex items-center gap-2">
                {isSearchOpen && (
                  <form onSubmit={handleSearchSubmit} className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="search"
                      autoFocus
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Buscar por nome ou email"
                      className="w-60 pl-9 pr-8 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {searchInput && (
                      <button
                        type="button"
                        onClick={handleSearchClear}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label="Limpar busca"
                        title="Limpar busca"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </form>
                )}
                <button
                  type="button"
                  onClick={handleSearchToggle}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors cursor-pointer"
                  aria-label={currentSearch ? 'Limpar busca' : 'Buscar'}
                  title={currentSearch ? 'Limpar busca' : 'Buscar'}
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </div>

            <FilterBuilder
              fields={filterableFields}
              value={filters}
              onChange={handleFiltersChange}
            />
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    E-mail
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Última visita
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Primeira visita
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversas
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Eventos
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => router.visit(`/contacts/${user.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-white text-sm font-medium ${getAvatarColor(
                            user.name
                          )}`}
                        >
                          {getInitials(user.name)}
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {user.name || 'Anônimo'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        {user.email ? (
                          <>
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                            {user.email}
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {user.companyName ? (
                        <Link
                          href={user.companyId ? `/contacts/companies/${user.companyId}` : '/contacts/companies'}
                          className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Building2 className="w-3.5 h-3.5" />
                          {user.companyName}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        {formatRelativeTime(user.lastSeenAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        {formatRelativeTime(user.createdAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
                        {user.conversationsCount}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Activity className="w-3.5 h-3.5 text-gray-400" />
                        {user.eventsCount}
                      </div>
                    </td>
                  </tr>
                ))}

                {users.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                      Nenhum usuário encontrado com os filtros aplicados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Infinite scroll sentinel and loading indicator */}
            <div ref={sentinelRef} className="py-4">
              {isLoadingMore && (
                <div className="flex justify-center">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {!hasMore && users.length > 0 && (
                <p className="text-center text-xs text-gray-400">
                  Todos os usuários carregados
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
